import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { ArrowLeft, Bell, Save, Gem, Flame, Package, Settings, Send } from 'lucide-react';
import { channelApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  isPushSupported,
  getPushPermission,
  subscribeToPush,
  unsubscribeFromPush,
  getCurrentPushSubscription,
  sendTestPush,
  registerServiceWorker,
} from '../services/pushManager';

const NOTIFY_LEVELS = [
  { id: 'all', label: 'All premium posts', desc: 'Get notified for every new premium channel post' },
  { id: 'high_value', label: 'High-value deals only', desc: 'Only deals above the threshold ($10M+)' },
  { id: 'selected_categories', label: 'Selected categories only', desc: 'Choose which categories to follow' },
  { id: 'none', label: 'No notifications', desc: 'Turn off all channel notifications' },
];

const CATEGORIES = [
  { id: 'gold_trade', label: 'Gold Trade', icon: Gem, color: 'text-amber-600' },
  { id: 'oil_energy', label: 'Oil & Energy', icon: Flame, color: 'text-blue-700' },
  { id: 'commodities', label: 'Commodities', icon: Package, color: 'text-green-600' },
  { id: 'machinery', label: 'Machinery', icon: Settings, color: 'text-purple-600' },
];

const NotificationSettingsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [testingPush, setTestingPush] = useState(false);
  const [pushSupported] = useState(isPushSupported());
  const [pushPermission, setPushPermission] = useState(getPushPermission());
  const [prefs, setPrefs] = useState({
    notify_level: 'all',
    categories: [],
    in_app: true,
    push: false,
    email: false,
  });

  useEffect(() => {
    registerServiceWorker();
  }, []);

  useEffect(() => {
    const fetchPrefs = async () => {
      try {
        const { data } = await channelApi.getNotifPreferences();
        // Also check if browser actually has a push subscription
        const browserSub = await getCurrentPushSubscription();
        setPrefs({
          notify_level: data.notify_level || 'all',
          categories: data.categories || [],
          in_app: data.in_app !== false,
          push: !!(data.push && browserSub),
          email: data.email || false,
        });
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchPrefs();
  }, []);

  const getToken = () => localStorage.getItem('access_token');

  const handlePushToggle = async (enable) => {
    if (!pushSupported) {
      toast.error('Push notifications are not supported in this browser');
      return;
    }
    if (pushPermission === 'denied') {
      toast.error('Notifications are blocked. Please enable them in your browser settings.');
      return;
    }

    setPushBusy(true);
    try {
      if (enable) {
        const result = await subscribeToPush(getToken());
        if (result.ok) {
          setPrefs(prev => ({ ...prev, push: true }));
          setPushPermission(getPushPermission());
          toast.success('Push notifications enabled');
        } else {
          toast.error(result.error || 'Failed to enable push');
        }
      } else {
        const result = await unsubscribeFromPush(getToken());
        if (result.ok) {
          setPrefs(prev => ({ ...prev, push: false }));
          toast.success('Push notifications disabled');
        } else {
          toast.error(result.error || 'Failed to disable push');
        }
      }
    } catch (err) {
      toast.error('Something went wrong');
    } finally {
      setPushBusy(false);
    }
  };

  const handleTestPush = async () => {
    setTestingPush(true);
    try {
      await sendTestPush(getToken());
      toast.success('Test notification sent! Check your notifications.');
    } catch (err) {
      toast.error(err.message || 'Test push failed');
    } finally {
      setTestingPush(false);
    }
  };

  const toggleCategory = (catId) => {
    setPrefs(prev => ({
      ...prev,
      categories: prev.categories.includes(catId)
        ? prev.categories.filter(c => c !== catId)
        : [...prev.categories, catId]
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await channelApi.updateNotifPreferences(prefs);
      toast.success('Notification preferences saved');
    } catch (e) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FB]">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-slate-200 rounded w-1/4" />
            <div className="h-40 bg-slate-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FB]">
      <Header />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-[#0A192F]">Notification Preferences</h1>
            <p className="text-xs text-slate-500">Control what alerts you receive</p>
          </div>
        </div>

        <div className="space-y-5">
          {/* Notification Level */}
          <div className="bg-white border border-slate-200 p-5">
            <h2 className="font-semibold text-[#0A192F] mb-4 flex items-center gap-2">
              <Bell className="w-5 h-5" /> Alert Level
            </h2>
            <div className="space-y-2">
              {NOTIFY_LEVELS.map(level => (
                <button
                  key={level.id}
                  onClick={() => setPrefs({ ...prefs, notify_level: level.id })}
                  className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                    prefs.notify_level === level.id
                      ? 'border-[#0A192F] bg-[#0A192F]/5'
                      : 'border-slate-100 hover:border-slate-200'
                  }`}
                  data-testid={`level-${level.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      prefs.notify_level === level.id ? 'border-[#0A192F]' : 'border-slate-300'
                    }`}>
                      {prefs.notify_level === level.id && <div className="w-2 h-2 rounded-full bg-[#0A192F]" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">{level.label}</p>
                      <p className="text-xs text-slate-500">{level.desc}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Category Selection - only show when "selected_categories" */}
          {prefs.notify_level === 'selected_categories' && (
            <div className="bg-white border border-slate-200 p-5">
              <h2 className="font-semibold text-[#0A192F] mb-4">Select Categories</h2>
              <div className="grid grid-cols-2 gap-3">
                {CATEGORIES.map(cat => {
                  const Icon = cat.icon;
                  const selected = prefs.categories.includes(cat.id);
                  return (
                    <button
                      key={cat.id}
                      onClick={() => toggleCategory(cat.id)}
                      className={`p-3 rounded-lg border-2 text-left flex items-center gap-2 transition-all ${
                        selected ? 'border-[#0A192F] bg-[#0A192F]/5' : 'border-slate-100 hover:border-slate-200'
                      }`}
                      data-testid={`cat-pref-${cat.id}`}
                    >
                      <Icon className={`w-5 h-5 ${cat.color}`} />
                      <span className="text-sm font-medium text-slate-800">{cat.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Delivery Channels */}
          <div className="bg-white border border-slate-200 p-5">
            <h2 className="font-semibold text-[#0A192F] mb-4">Delivery Channels</h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-slate-800">In-App Notifications</p>
                  <p className="text-xs text-slate-500">Bell icon alerts and notification center</p>
                </div>
                <Switch
                  checked={prefs.in_app}
                  onCheckedChange={v => setPrefs({ ...prefs, in_app: v })}
                  data-testid="toggle-in-app"
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-slate-800">Browser Push Notifications</p>
                  <p className="text-xs text-slate-500">
                    {!pushSupported
                      ? 'Not supported in this browser'
                      : pushPermission === 'denied'
                        ? 'Blocked — enable in browser settings'
                        : 'Desktop alerts even when tab is inactive'}
                  </p>
                </div>
                <Switch
                  checked={prefs.push}
                  onCheckedChange={handlePushToggle}
                  disabled={!pushSupported || pushPermission === 'denied' || pushBusy}
                  data-testid="toggle-push"
                />
              </div>
              {prefs.push && (
                <div className="pl-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleTestPush}
                    disabled={testingPush}
                    data-testid="test-push-btn"
                    className="text-xs"
                  >
                    <Send className="w-3.5 h-3.5 mr-1.5" />
                    {testingPush ? 'Sending...' : 'Send Test Notification'}
                  </Button>
                </div>
              )}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg opacity-60">
                <div>
                  <p className="text-sm font-medium text-slate-800">Email Notifications</p>
                  <p className="text-xs text-slate-500">Coming soon — Email digest of important deals</p>
                </div>
                <Switch checked={false} disabled />
              </div>
            </div>
          </div>

          {/* Save */}
          <Button
            className="w-full bg-[#0A192F] hover:bg-[#172A46] text-white h-11 font-semibold"
            onClick={handleSave}
            disabled={saving}
            data-testid="save-preferences-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Preferences'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettingsPage;
